import {
  lookupBridgeRule,
  type BridgeLifecycle,
  type BridgeMatrix as CoreBridgeMatrix,
  type NeutralizationTheoremCode,
} from "@csk/core";
import {
  assertWasmKernelContract,
  assertWasmSeamBridgeContract,
  createCoreBackedKernel,
  createKernel,
  createPureTsBoundaryReceipt,
  SafetyKernel,
  type KernelInitResult,
  type NativeWasmBoundaryReceipt,
  type WasmBridgeAdmissionOutcome,
  type WasmBridgeAdmissionReceipt,
  type WasmKernelValidationReceipt,
  type WasmSeamBridgeProbe,
  type WasmSeamBridgeValidationReceipt,
  type WasmSquareDecision,
} from "@csk/wasm";
import {
  getTheoremFidelityReport,
  type TheoremFidelityReport,
} from "@csk/wasm/theorem-accelerator";
import { createWasmBridgeProofCarrier, type WasmBridgeProofCarrier } from "./wasmBridgeProof.js";
import {
  allowedTransitionSquareContext,
  forbiddenTransitionSquareContext,
  type WasmSquareFortification,
} from "./wasmSquareFortification.js";

export interface RuntimeGuaranteeComparison {
  readonly code: string;
  readonly name: string;
  readonly mode: TheoremFidelityReport["mode"];
  readonly rawTs: TheoremFidelityReport;
  readonly tsWasm: TheoremFidelityReport;
  readonly hardenedByWasm: boolean;
  readonly summary: string;
}

export interface DemoStructuralKernel {
  readonly adapter: SafetyKernel;
  readonly backend: KernelInitResult["backend"];
  readonly theoremFidelity: readonly TheoremFidelityReport[];
  readonly contractValidation: WasmKernelValidationReceipt;
  readonly seamBridgeValidation: WasmSeamBridgeValidationReceipt;
  readonly nativeBoundary: NativeWasmBoundaryReceipt;
}

export interface WasmPathCertification {
  readonly theoremCode: string;
  readonly theoremName: string;
  readonly backend: KernelInitResult["backend"];
  readonly fidelity: TheoremFidelityReport["fidelity"];
  readonly trustZone: TheoremFidelityReport["trustZone"];
  readonly seamBridge: WasmSeamBridgeProbe;
  readonly squareDecision: WasmSquareDecision;
  readonly squareFortified: boolean;
  readonly bridgeProof: WasmBridgeProofCarrier;
  readonly accepted: boolean;
  readonly transitions: number;
  readonly reason: string | null;
}

export type RuntimeHardenedTheoremCode = "R1" | "R2" | "I1" | "I2" | "C";

export interface WasmHardeningProbe {
  readonly theoremCode: RuntimeHardenedTheoremCode;
  readonly theoremName: string;
  readonly backend: KernelInitResult["backend"];
  readonly fidelity: TheoremFidelityReport["fidelity"];
  readonly trustZone: TheoremFidelityReport["trustZone"];
  readonly seamBridge: WasmSeamBridgeProbe;
  readonly accepted: boolean;
  readonly adversarialCheckRejected: boolean;
  readonly summary: string;
  readonly detail: string;
}

export interface WasmSeamBridgeStateProbe {
  readonly theoremCode: NeutralizationTheoremCode;
  readonly theoremName: string;
  readonly backend: KernelInitResult["backend"];
  readonly seamBridge: WasmSeamBridgeProbe;
  readonly admission: WasmBridgeAdmissionOutcome;
  readonly receipt: WasmBridgeAdmissionReceipt;
  readonly lifecycle: BridgeLifecycle;
  readonly remainingUses: number;
  readonly replayRejected: boolean;
  readonly missingWitnessRejected: boolean;
  readonly revokedWitnessRejected: boolean;
  readonly forbiddenSquareRejected: boolean;
  readonly accepted: boolean;
  readonly detail: string;
}

const RAW_TS_FIDELITY = getTheoremFidelityReport("pure-ts");
const TS_WASM_FIDELITY = getTheoremFidelityReport("wasm");

function indexReports(
  reports: readonly TheoremFidelityReport[],
): ReadonlyMap<string, TheoremFidelityReport> {
  return new Map(reports.map((report) => [report.code, report]));
}

const TS_WASM_BY_CODE = indexReports(TS_WASM_FIDELITY);

function reportFor(reports: readonly TheoremFidelityReport[], code: string): TheoremFidelityReport {
  const report = reports.find((entry) => entry.code === code);
  if (report === undefined) {
    throw new Error(`TS-WASM fidelity report is missing ${code}`);
  }
  return report;
}

function seamBridgeProbeFor(
  kernel: DemoStructuralKernel,
  theoremCode: NeutralizationTheoremCode,
): WasmSeamBridgeProbe {
  const probe = kernel.seamBridgeValidation.probes.find(
    (entry) => entry.theoremCode === theoremCode,
  );
  if (probe === undefined) {
    throw new Error(`TS-WASM seam/bridge receipt is missing ${theoremCode}`);
  }
  return probe;
}

function hardeningSummary(rawTs: TheoremFidelityReport, tsWasm: TheoremFidelityReport): string {
  if (rawTs.fidelity === tsWasm.fidelity) {
    return "No runtime relocation: this theorem remains a compile-time TypeScript guarantee.";
  }
  return "Runtime enforcement state moves from the JavaScript heap into the WASM trust zone.";
}

export const RUNTIME_GUARANTEE_COMPARISON: readonly RuntimeGuaranteeComparison[] =
  RAW_TS_FIDELITY.map((rawTs) => {
    const tsWasm = TS_WASM_BY_CODE.get(rawTs.code);
    if (tsWasm === undefined) {
      throw new Error(`TS-WASM fidelity comparison is missing ${rawTs.code}`);
    }
    return {
      code: rawTs.code,
      name: rawTs.name,
      mode: rawTs.mode,
      rawTs,
      tsWasm,
      hardenedByWasm: rawTs.trustZone !== tsWasm.trustZone,
      summary: hardeningSummary(rawTs, tsWasm),
    };
  });

let hybridKernelPromise: Promise<DemoStructuralKernel> | null = null;

export function createSynchronousStructuralKernel(): DemoStructuralKernel {
  const kernel = createCoreBackedKernel();
  const contractValidation = assertWasmKernelContract(kernel, "pure-ts");
  const seamBridgeValidation = assertWasmSeamBridgeContract(kernel, "pure-ts");
  return {
    adapter: new SafetyKernel(kernel),
    backend: "pure-ts",
    theoremFidelity: RAW_TS_FIDELITY,
    contractValidation,
    seamBridgeValidation,
    nativeBoundary: createPureTsBoundaryReceipt(),
  };
}

export async function loadDemoStructuralKernel(): Promise<DemoStructuralKernel> {
  hybridKernelPromise ??= createKernel("theorem-hybrid").then((result) => ({
    adapter: new SafetyKernel(result.kernel),
    backend: result.backend,
    contractValidation: result.contractValidation,
    seamBridgeValidation: result.seamBridgeValidation,
    nativeBoundary: result.nativeBoundary,
    theoremFidelity: result.theoremFidelity,
  }));
  return await hybridKernelPromise;
}

function certificationFor(
  kernel: DemoStructuralKernel,
  squareContext: WasmSquareFortification,
  accepted: boolean,
  transitions: number,
  reason: string | null,
): WasmPathCertification {
  const { theoremCode, generatorCode } = squareContext;
  const fidelity = reportFor(kernel.theoremFidelity, theoremCode);
  const squareDecision = kernel.adapter.squares.decide(theoremCode, generatorCode);
  const bridgeProof = createWasmBridgeProofCarrier(kernel.adapter, theoremCode, generatorCode);
  const bridgeAccepted = bridgeProof.admission.status === "admitted";
  const squareFortified =
    squareDecision.status === "forbidden"
      ? bridgeProof.admission.status === "rejected-forbidden-square"
      : bridgeAccepted;
  return {
    theoremCode,
    theoremName: fidelity.name,
    backend: kernel.backend,
    fidelity: fidelity.fidelity,
    trustZone: fidelity.trustZone,
    seamBridge: seamBridgeProbeFor(kernel, theoremCode),
    squareDecision,
    squareFortified,
    bridgeProof,
    accepted: accepted && bridgeAccepted && squareDecision.status !== "forbidden",
    transitions,
    reason:
      reason ??
      (squareDecision.status === "forbidden"
        ? squareDecision.reason
        : bridgeAccepted
          ? null
          : bridgeProof.admission.reason),
  };
}

export function certifyAllowedTransitionPath(
  matrix: CoreBridgeMatrix,
  path: readonly string[],
): WasmPathCertification {
  const kernel = createSynchronousStructuralKernel();
  const [initial, ...rest] = path;
  if (initial === undefined) {
    return certificationFor(kernel, { theoremCode: "I2", generatorCode: "G1" }, true, 0, null);
  }

  const machine = kernel.adapter.invPreserve.createMachine(initial);
  let transitions = 0;
  let from = initial;
  let lastSquareContext: WasmSquareFortification = {
    theoremCode: "I2",
    generatorCode: "G1",
  };

  for (const to of rest) {
    const rule = lookupBridgeRule(matrix, from, to);
    let squareContext: WasmSquareFortification;
    if (rule.status !== "allowed") {
      squareContext = forbiddenTransitionSquareContext(rule);
      return certificationFor(
        kernel,
        squareContext,
        false,
        transitions,
        rule.reason ?? `matrix forbids ${from} -> ${to}`,
      );
    }
    squareContext = allowedTransitionSquareContext(from, to);
    const squareDecision = kernel.adapter.squares.decide(
      squareContext.theoremCode,
      squareContext.generatorCode,
    );
    if (squareDecision.status === "forbidden") {
      return certificationFor(kernel, squareContext, false, transitions, squareDecision.reason);
    }
    lastSquareContext = squareContext;
    kernel.adapter.invPreserve.allowTransition(machine, from, to);
    try {
      kernel.adapter.invPreserve.transition(machine, to);
      transitions += 1;
    } catch (error) {
      return certificationFor(
        kernel,
        { theoremCode: "I2", generatorCode: "G3" },
        false,
        transitions,
        error instanceof Error ? error.message : "TS-WASM transition rejected",
      );
    }
    from = to;
  }

  return certificationFor(kernel, lastSquareContext, true, transitions, null);
}

function probeReceipt(
  kernel: DemoStructuralKernel,
  theoremCode: RuntimeHardenedTheoremCode,
  accepted: boolean,
  adversarialCheckRejected: boolean,
  summary: string,
  detail: string,
): WasmHardeningProbe {
  const fidelity = reportFor(kernel.theoremFidelity, theoremCode);
  return {
    theoremCode,
    theoremName: fidelity.name,
    backend: kernel.backend,
    fidelity: fidelity.fidelity,
    trustZone: fidelity.trustZone,
    seamBridge: seamBridgeProbeFor(kernel, theoremCode),
    accepted,
    adversarialCheckRejected,
    summary,
    detail,
  };
}

export function runResourceConsumptionProbe(
  kernel: DemoStructuralKernel = createSynchronousStructuralKernel(),
): WasmHardeningProbe {
  const handle = kernel.adapter.resConsume.allocate("demo-unit");
  const firstUse = kernel.adapter.resConsume.consume(handle);
  let replayRejected = false;
  try {
    kernel.adapter.resConsume.consume(handle);
  } catch {
    replayRejected = true;
  }
  return probeReceipt(
    kernel,
    "R1",
    firstUse === "demo-unit" && replayRejected && !kernel.adapter.resConsume.isLive(handle),
    replayRejected,
    "Exactly-once resource consumption",
    "The first consume succeeds; replaying the same handle is rejected by the TS-WASM contract.",
  );
}

export function runResourceReleaseProbe(
  kernel: DemoStructuralKernel = createSynchronousStructuralKernel(),
): WasmHardeningProbe {
  const managed = kernel.adapter.resRelease.create("demo-managed-resource");
  kernel.adapter.resRelease.useAndRelease(managed);
  const released = kernel.adapter.resRelease.isReleased(managed);
  let replayRejected = false;
  try {
    kernel.adapter.resRelease.useAndRelease(managed);
  } catch {
    replayRejected = true;
  }
  return probeReceipt(
    kernel,
    "R2",
    released && replayRejected,
    replayRejected,
    "Exactly-once resource release",
    "The managed resource is released once; a second use-and-release is rejected by the TS-WASM contract.",
  );
}

export function runCompositionProbe(
  kernel: DemoStructuralKernel = createSynchronousStructuralKernel(),
): WasmHardeningProbe {
  const parse = kernel.adapter.compose.registerOperation(
    "parse",
    "HttpRequest",
    "AuthenticatedRequest",
  );
  const authorize = kernel.adapter.compose.registerOperation(
    "authorize",
    "AuthenticatedRequest",
    "AuthorizedRequest",
  );
  const execute = kernel.adapter.compose.registerOperation("execute", "SqlQuery", "DbResult");
  const chain = kernel.adapter.compose.chain(parse, authorize);
  const chainDescription = kernel.adapter.compose.validateChain(chain);
  let mismatchRejected = false;
  try {
    kernel.adapter.compose.chain(parse, execute);
  } catch {
    mismatchRejected = true;
  }
  return probeReceipt(
    kernel,
    "C",
    chainDescription.includes("HttpRequest") && mismatchRejected,
    mismatchRejected,
    "Type-compatible composition only",
    "The compatible chain is accepted; the HttpRequest -> SqlQuery mismatch is rejected.",
  );
}

export function runInvariantEstablishmentProbe(
  kernel: DemoStructuralKernel = createSynchronousStructuralKernel(),
): WasmHardeningProbe {
  const builder = kernel.adapter.invEstablish.createBuilder();
  kernel.adapter.invEstablish.addCheck(builder, "balance-non-negative");
  const state = kernel.adapter.invEstablish.establish(builder, "Account(balance=100)");
  const stateName = kernel.adapter.invEstablish.stateName(state);
  let builderReuseRejected = false;
  try {
    kernel.adapter.invEstablish.establish(builder, "Account(balance=200)");
  } catch {
    builderReuseRejected = true;
  }
  return probeReceipt(
    kernel,
    "I1",
    stateName === "Account(balance=100)" && builderReuseRejected,
    builderReuseRejected,
    "Single-use invariant establishment",
    "The builder establishes one validated state; reusing the consumed builder is rejected by the TS-WASM contract.",
  );
}

export function runInvariantPreservationProbe(
  kernel: DemoStructuralKernel = createSynchronousStructuralKernel(),
): WasmHardeningProbe {
  const machine = kernel.adapter.invPreserve.createMachine("Draft");
  kernel.adapter.invPreserve.allowTransition(machine, "Draft", "Review");
  let allowedTransitionAccepted = true;
  try {
    kernel.adapter.invPreserve.transition(machine, "Review");
  } catch {
    allowedTransitionAccepted = false;
  }
  let unregisteredTransitionRejected = false;
  try {
    kernel.adapter.invPreserve.transition(machine, "Approved");
  } catch {
    unregisteredTransitionRejected = true;
  }
  return probeReceipt(
    kernel,
    "I2",
    allowedTransitionAccepted && unregisteredTransitionRejected,
    unregisteredTransitionRejected,
    "Registered invariant transitions only",
    "The allowed Draft -> Review transition is accepted; the unregistered Review -> Approved jump is rejected.",
  );
}

export function runSeamBridgeStateProbe(
  kernel: DemoStructuralKernel = createSynchronousStructuralKernel(),
  theoremCode: NeutralizationTheoremCode = "P",
): WasmSeamBridgeStateProbe {
  const scope = `wasm-seam:${theoremCode}`;
  const seamBridge = seamBridgeProbeFor(kernel, theoremCode);
  const seam = kernel.adapter.seams.define(theoremCode);
  const witness = kernel.adapter.seams.mintWitness(theoremCode, scope, 1);
  const bridge = kernel.adapter.seams.propose(seam, witness, "typescript-host", "wasm-kernel");
  const admission = kernel.adapter.seams.admit(bridge, scope);
  const receipt = kernel.adapter.seams.receipt(bridge);
  const lifecycle = kernel.adapter.seams.lifecycle(bridge);
  const remainingUses = kernel.adapter.seams.remainingWitnessUses(witness);
  const replay = kernel.adapter.seams.admit(bridge, scope);

  const missingBridge = kernel.adapter.seams.propose(seam, null, "typescript-host", "wasm-kernel");
  const missing = kernel.adapter.seams.admit(missingBridge, scope);

  const revokedWitness = kernel.adapter.seams.mintWitness(theoremCode, scope, 1);
  kernel.adapter.seams.revokeWitness(revokedWitness);
  const revokedBridge = kernel.adapter.seams.propose(
    seam,
    revokedWitness,
    "typescript-host",
    "wasm-kernel",
  );
  const revoked = kernel.adapter.seams.admit(revokedBridge, scope);

  const forbiddenWitness = kernel.adapter.seams.mintWitness(theoremCode, scope, 1);
  const forbiddenBridge = kernel.adapter.seams.propose(
    seam,
    forbiddenWitness,
    "typescript-host",
    "wasm-kernel",
  );
  const forbidden = kernel.adapter.seams.admitWithSquare(
    forbiddenBridge,
    scope,
    seamBridge.forbiddenGenerator,
  );

  const replayRejected = replay.status === "rejected-invalid-lifecycle";
  const missingWitnessRejected = missing.status === "rejected-missing-witness";
  const revokedWitnessRejected = revoked.status === "rejected-invalid-lifecycle";
  const forbiddenSquareRejected = forbidden.status === "rejected-forbidden-square";
  const accepted =
    admission.status === "admitted" &&
    lifecycle === "admitted" &&
    remainingUses === 0 &&
    replayRejected &&
    missingWitnessRejected &&
    revokedWitnessRejected &&
    forbiddenSquareRejected;

  return {
    theoremCode,
    theoremName: seamBridge.theoremName,
    backend: kernel.backend,
    seamBridge,
    admission,
    receipt,
    lifecycle,
    remainingUses,
    replayRejected,
    missingWitnessRejected,
    revokedWitnessRejected,
    forbiddenSquareRejected,
    accepted,
    detail:
      "Stateful seam handles, bridge-witness handles, admission receipts, and replay refusal are exercised through the typed TS-WASM adapter.",
  };
}
